import type { ReactNode } from 'react'
import { Platform, Pressable, ScrollView, Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Head from 'expo-router/head'
import { StatusBar } from 'expo-status-bar'
import { Trans, useTranslation } from 'react-i18next'

import { Button } from '../../../components/ui/Button'
import { colors } from '../../../theme'
import { body3, headline1, label1, label2 } from '../../../theme/typography'
import { RecordIcon } from '../components/RecordIcon'
import { VoiceAssessmentBetaBadge } from '../components/VoiceAssessmentBetaBadge'
import { VoiceAssessmentDurationRow } from '../components/VoiceAssessmentDurationRow'

const NON_SCALING_TEXT_PROPS = {
  allowFontScaling: false,
  maxFontSizeMultiplier: 1,
} as const

const PROGRESS_BAR_HEIGHT = 9
const CTA_BUTTON_HEIGHT = 56
const CONTROL_BORDER_RADIUS = 8

interface ProgressBarProps {
  totalSteps: number
  currentStep: number
}

function ProgressBar({ totalSteps, currentStep }: ProgressBarProps) {
  const stepNumbers = Array.from(
    { length: totalSteps },
    (_, index) => index + 1,
  )

  return (
    <View className="flex-row items-center gap-2">
      {stepNumbers.map((stepNumber) => (
        <View
          key={`assessment-progress-${stepNumber}`}
          className={`flex-1 rounded-full ${stepNumber <= currentStep ? 'bg-primary' : 'bg-border'}`}
          style={{ height: PROGRESS_BAR_HEIGHT }}
        />
      ))}
    </View>
  )
}

interface StepItemProps {
  number: number
  children: ReactNode
}

function StepItem({ number, children }: StepItemProps) {
  return (
    <View className="flex-row items-start gap-3">
      <View
        className="rounded-full items-center justify-center"
        style={{
          width: 24,
          height: 24,
          minWidth: 24,
          backgroundColor: colors.coreSecondary,
        }}
      >
        <Text
          {...NON_SCALING_TEXT_PROPS}
          style={{
            fontFamily: 'Cairo_700Bold',
            fontSize: 12,
            lineHeight: Platform.OS === 'ios' ? 16 : 14,
            color: colors.primaryForeground,
          }}
        >
          {number}
        </Text>
      </View>
      <Text
        {...NON_SCALING_TEXT_PROPS}
        style={[body3, { color: colors.textBody, flex: 1 }]}
      >
        {children}
      </Text>
    </View>
  )
}

interface VoiceAssessmentIntroScreenProps {
  onStart: () => void
  onSkip: () => void
  isStartingSession?: boolean
  expectationStandalone?: {
    totalSteps: number
    measurementCount: number
  } | null
  timeEstimateMinutes?: number | null
  totalSteps?: number
  currentStep?: number
  isPostPaymentFlow?: boolean
  /** Optional left title beside Skip (standalone flow may omit). */
  stepHeaderLabel?: string
}

export function VoiceAssessmentIntroScreen({
  onStart,
  onSkip,
  isStartingSession = false,
  expectationStandalone = null,
  timeEstimateMinutes = null,
  totalSteps = 7,
  currentStep = 3,
  isPostPaymentFlow = false,
  stepHeaderLabel,
}: VoiceAssessmentIntroScreenProps) {
  const { t } = useTranslation(['onboarding', 'common'])
  const insets = useSafeAreaInsets()
  const footerPaddingBottom = Math.max(insets.bottom, 16)

  return (
    <View
      className="flex-1 bg-background-primary"
      style={{
        flex: 1,
        backgroundColor: colors.backgroundPrimary,
        paddingTop: insets.top,
      }}
    >
      <Head>
        <title>{t('onboarding:voice_assessment.page_title')}</title>
      </Head>
      <StatusBar style="dark" />

      <View className="px-6 pt-6">
        <View className="flex-row items-center justify-between mb-3 gap-2">
          {stepHeaderLabel != null ? (
            <Text
              {...NON_SCALING_TEXT_PROPS}
              numberOfLines={2}
              style={[label2, { color: colors.textMuted, flex: 1 }]}
            >
              {stepHeaderLabel}
            </Text>
          ) : (
            <View className="flex-1" />
          )}
          {isPostPaymentFlow ? (
            <Pressable
              onPress={onSkip}
              className="active:opacity-60"
              hitSlop={8}
            >
              <Text
                {...NON_SCALING_TEXT_PROPS}
                style={[label2, { color: colors.textBody }]}
              >
                {t('onboarding:voice_assessment.skip_header')}
              </Text>
            </Pressable>
          ) : null}
        </View>
        {isPostPaymentFlow && (
          <ProgressBar totalSteps={totalSteps} currentStep={currentStep} />
        )}
      </View>

      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        style={{ flex: 1, backgroundColor: colors.backgroundPrimary }}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 24,
          paddingTop: 24,
          paddingBottom: 24,
        }}
        showsVerticalScrollIndicator={false}
      >
        <View className="items-center" style={{ gap: 26, width: '100%' }}>
          <RecordIcon size={80} />
          <View className="items-center" style={{ gap: 8, width: '100%' }}>
            <Text
              {...NON_SCALING_TEXT_PROPS}
              className="text-center"
              style={[headline1, { color: colors.textHeader }]}
            >
              {t('onboarding:voice_assessment.title')}
            </Text>
            <VoiceAssessmentBetaBadge
              size="large"
              testID="voice-assessment-intro-beta-badge"
            />
          </View>
          <Text
            {...NON_SCALING_TEXT_PROPS}
            className="text-center"
            style={{
              fontFamily: 'OpenSans_400Regular',
              fontSize: 16,
              lineHeight: 18,
              color: colors.textBody,
            }}
          >
            {t('onboarding:voice_assessment.description')}
          </Text>

          <View
            className="rounded-2xl p-5"
            style={{
              borderRadius: 16,
              gap: 16,
              width: '100%',
              backgroundColor: colors.background,
            }}
          >
            <View
              style={{
                gap: 6,
                paddingBottom: 16,
                borderBottomWidth: 1,
                borderBottomColor: colors.border,
              }}
            >
              <Text
                {...NON_SCALING_TEXT_PROPS}
                style={[label2, { color: colors.textHeader, fontSize: 14 }]}
              >
                {t('onboarding:voice_assessment.later_quiet_title')}
              </Text>
              <Text
                {...NON_SCALING_TEXT_PROPS}
                style={{
                  fontFamily: 'OpenSans_400Regular',
                  fontSize: 14,
                  lineHeight: 18,
                  color: colors.textBody,
                }}
              >
                {t('onboarding:voice_assessment.later_quiet_note')}
              </Text>
            </View>
            <Text
              {...NON_SCALING_TEXT_PROPS}
              style={[label2, { color: colors.textBody, fontSize: 14 }]}
            >
              {t('onboarding:voice_assessment.what_to_expect_heading')}
            </Text>
            <StepItem number={1}>
              {t('onboarding:voice_assessment.step_1')}
            </StepItem>
            <StepItem number={2}>
              {expectationStandalone != null ? (
                t('onboarding:voice_assessment.step_2_standalone', {
                  count: expectationStandalone.measurementCount,
                })
              ) : (
                <Trans
                  i18nKey="voice_assessment.step_2"
                  ns="onboarding"
                  components={{
                    bold: (
                      <Text
                        style={{
                          fontFamily: 'Cairo_700Bold',
                          fontSize: 14,
                          letterSpacing: 0.4,
                          color: colors.textBody,
                        }}
                      />
                    ),
                  }}
                />
              )}
            </StepItem>
            <StepItem number={3}>
              {t('onboarding:voice_assessment.step_3')}
            </StepItem>
          </View>

          <VoiceAssessmentDurationRow
            timeEstimateMinutes={timeEstimateMinutes}
          />
        </View>
      </ScrollView>
      <View
        style={{
          paddingHorizontal: 24,
          paddingBottom: footerPaddingBottom,
          gap: 12,
        }}
      >
        <>
          <Button
            label={t('onboarding:voice_assessment.start_cta')}
            onPress={onStart}
            variant="primary"
            size="md"
            loading={isStartingSession}
            disabled={isStartingSession}
            containerClassName="w-full"
          />

          <Pressable
            className="rounded-[8px] border items-center justify-center"
            style={{
              height: CTA_BUTTON_HEIGHT,
              borderRadius: CONTROL_BORDER_RADIUS,
              borderColor: colors.textHeader,
            }}
            onPress={onSkip}
          >
            <Text
              {...NON_SCALING_TEXT_PROPS}
              style={[label1, { color: colors.textBody }]}
            >
              {t('onboarding:voice_assessment.skip_cta')}
            </Text>
          </Pressable>
        </>
      </View>
    </View>
  )
}
